'use client'

/* eslint-disable @next/next/no-img-element */
import { useGyymClient } from '@gyym/sdk-react'
import { cn } from '@gyym/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/nextjs'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  EMPLOYEE_COUNT_OPTIONS,
  FORM_ANCHOR_ID,
  LINKS,
  lpImage,
} from '@/app/(sfc)/corp/(lp)/_data'
import { getValidationErrors } from '@/lib/form'
import { pushIntroductionMaterialRequestEvent } from '@/lib/googleTagManager'

import { GRADIENT_PRIMARY, GRADIENT_SECTION } from '../styles'
import {
  CORP_INQUIRY_FIELDS,
  corpInquirySchema,
  type CorpInquiryValues,
} from './schema'

const isCorpInquiryField = (key: string): key is keyof CorpInquiryValues =>
  (CORP_INQUIRY_FIELDS as string[]).includes(key)

const labelClassName = 'text-[16px] font-medium leading-[25.6px] text-[#32355B]'
const inputClassName =
  'w-full rounded-[4px] border-0 bg-white px-[15px] py-[10px] text-[15px] font-normal leading-[21px] text-[#333333] outline-none placeholder:text-[#757575] focus:ring-2 focus:ring-[#FF0BA4]/40'

const FieldLabel = ({
  children,
  htmlFor,
}: {
  children: React.ReactNode
  htmlFor?: string
}) => (
  <label htmlFor={htmlFor} className="mb-[10px] flex items-start">
    <span className={labelClassName}>{children}</span>
    <span className={cn(labelClassName, 'text-[#FF0000]')}>*</span>
  </label>
)

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <p role="alert" className="mt-1 text-[12px] leading-[18px] text-[#FF0000]">
      {message}
    </p>
  ) : null

/** GYYM法人会員サービス 紹介資料請求フォーム */
export const ContactFormSection = () => {
  const client = useGyymClient()
  const [isCompleted, setIsCompleted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CorpInquiryValues>({
    resolver: zodResolver(corpInquirySchema),
    mode: 'onBlur',
    defaultValues: {
      companyName: '',
      departmentName: '',
      contactPersonName: '',
      email: '',
      message: '',
    },
  })

  // 受付 API は IP 単位でレートリミットされるため、server action (Next サーバーの IP に集約される) ではなく
  // ブラウザから直接送信する
  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    try {
      await client.user.inquiries.submitIntroductionMaterialRequest(values)
      pushIntroductionMaterialRequestEvent(values.employeeCount)
      setIsCompleted(true)
      // フォームが短い完了表示に置き換わるので、見出しまで戻す
      document
        .getElementById(FORM_ANCHOR_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } catch (e) {
      if (client.isHttpError(e) && e.status === 422) {
        const fieldErrors = Object.entries(getValidationErrors(e)).filter(
          (entry): entry is [keyof CorpInquiryValues, string[]] =>
            isCorpInquiryField(entry[0]) && !!entry[1][0],
        )
        fieldErrors.forEach(([field, messages]) =>
          setError(field, { type: 'serverError', message: messages[0] }),
        )
        if (fieldErrors.length > 0) return
      }
      if (client.isHttpError(e) && e.status === 429) {
        setSubmitError(
          '送信が集中しています。しばらく時間をおいてから再度お試しください。',
        )
        return
      }
      Sentry.captureException(e)
      setSubmitError('送信に失敗しました。時間をおいて再度お試しください。')
    }
  })

  return (
    <section
      id="contact"
      className={cn(
        'relative z-0 flex flex-col items-center overflow-hidden py-[60px] max-[540px]:py-[50px]',
        GRADIENT_SECTION,
      )}
    >
      {/* 透かしの GYYM マーク */}
      <img
        src={lpImage('gyym-mark.svg')}
        alt=""
        className="pointer-events-none absolute left-[calc(50%+127px)] top-[496px] z-[-1] size-[593px] opacity-10 max-[820px]:left-[calc(50%-209px)] max-[820px]:top-[509px] max-[540px]:left-[calc(50%-26px)] max-[540px]:top-[52px] max-[540px]:size-[375px]"
      />

      <div className="flex w-[1280px] max-w-full flex-col items-center px-[30px]">
        <div
          id={FORM_ANCHOR_ID}
          className="mb-[50px] flex scroll-mt-5 flex-col items-center"
        >
          <p className="mb-5 text-center text-[32px] font-bold leading-[51.2px] text-[#32355B] max-[820px]:text-[36px] max-[820px]:leading-[57.6px] max-[540px]:mb-[10px] max-[540px]:text-[24px] max-[540px]:leading-[38.4px]">
            GYYM法人会員サービス
            <br />
            紹介資料請求
          </p>
        </div>

        {isCompleted ? (
          <div
            role="status"
            className="mb-5 flex w-[600px] max-w-full flex-col items-center rounded-sm bg-white px-6 py-10 text-center"
          >
            <p className="text-[18px] font-medium leading-[30.6px] text-[#32355B] max-[540px]:text-[16px] max-[540px]:leading-[27.2px]">
              送信が完了しました。
              <br />
              担当者よりご連絡いたします。
            </p>
          </div>
        ) : (
          <form
            noValidate
            onSubmit={onSubmit}
            className="mb-5 flex w-[600px] max-w-full flex-col items-start max-[540px]:items-center"
          >
            <div className="mb-5 flex w-full flex-col items-start">
              <FieldLabel htmlFor="corp-inquiry-company">会社名</FieldLabel>
              <input
                id="corp-inquiry-company"
                type="text"
                placeholder="例：GYYM株式会社"
                autoComplete="organization"
                className={inputClassName}
                {...register('companyName')}
              />
              <ErrorMessage message={errors.companyName?.message} />
            </div>

            <div className="mb-5 flex w-full flex-col items-start">
              <FieldLabel htmlFor="corp-inquiry-department">部署名</FieldLabel>
              <input
                id="corp-inquiry-department"
                type="text"
                placeholder="例：総務部"
                autoComplete="organization-title"
                className={inputClassName}
                {...register('departmentName')}
              />
              <ErrorMessage message={errors.departmentName?.message} />
            </div>

            <div className="mb-5 flex w-full flex-col items-start">
              <FieldLabel htmlFor="corp-inquiry-name">ご担当者様</FieldLabel>
              <input
                id="corp-inquiry-name"
                type="text"
                placeholder="名字 名前"
                autoComplete="name"
                className={inputClassName}
                {...register('contactPersonName')}
              />
              <ErrorMessage message={errors.contactPersonName?.message} />
            </div>

            <fieldset className="mb-5 flex w-full flex-col items-start">
              <legend className="mb-[10px] flex items-start">
                <span className={labelClassName}>ご従業員様数</span>
                <span className={cn(labelClassName, 'text-[#FF0000]')}>*</span>
              </legend>
              <div className="mb-5 flex w-full flex-col items-start">
                {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                  <label
                    key={option}
                    className="flex w-full cursor-pointer items-center p-[10px]"
                  >
                    <input
                      type="radio"
                      value={option}
                      className="mr-[10px] size-[15px] shrink-0 accent-[#FF0BA4]"
                      {...register('employeeCount')}
                    />
                    <span className="font-lato text-[16px] font-normal leading-[22.4px] text-[#32355B]">
                      {option}
                    </span>
                  </label>
                ))}
                <ErrorMessage message={errors.employeeCount?.message} />
              </div>
            </fieldset>

            <div className="mb-5 flex w-full flex-col items-start">
              <FieldLabel htmlFor="corp-inquiry-email">
                ご勤務先メールアドレス
              </FieldLabel>
              <input
                id="corp-inquiry-email"
                type="email"
                placeholder="Email@address"
                autoComplete="email"
                className={inputClassName}
                {...register('email')}
              />
              <ErrorMessage message={errors.email?.message} />
            </div>

            <div className="mb-5 flex w-full flex-col items-start">
              <FieldLabel htmlFor="corp-inquiry-message">
                その他（ご質問などありましたら、ご自由にお書きください）
              </FieldLabel>
              <textarea
                id="corp-inquiry-message"
                placeholder="お問い合わせ内容を記入してください"
                className={cn(inputClassName, 'h-40 resize-y p-[10px]')}
                {...register('message')}
              />
              <ErrorMessage message={errors.message?.message} />
            </div>

            <label className="mb-[10px] flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="mr-[10px] size-[13px] shrink-0 accent-[#FF0BA4]"
                {...register('privacyPolicyAgreed')}
              />
              <span className="text-[15px] font-medium leading-[21px] text-[#32355B]">
                プライバシーポリシーへの同意
              </span>
            </label>
            <p className="mb-5 flex w-full items-center justify-center text-center text-[12px] leading-[16.8px] text-[#32355B]">
              プライバシーポリシーは
              <a
                href={LINKS.policy}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#EC0D61]"
              >
                こちら
              </a>
              からご確認ください。
            </p>
            <ErrorMessage message={errors.privacyPolicyAgreed?.message} />
            <ErrorMessage message={submitError ?? undefined} />

            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                'mt-2 rounded-md px-[30px] py-[10px] text-[16px] font-medium leading-[21px] text-white transition-transform duration-300 hover:scale-105 disabled:opacity-60 max-[540px]:w-[252px] max-[540px]:max-w-[80%] max-[540px]:shadow-[0_2px_5px_rgba(0,0,0,0.2)]',
                GRADIENT_PRIMARY,
              )}
            >
              {isSubmitting ? '送信中…' : '送信'}
            </button>
          </form>
        )}
      </div>
    </section>
  )
}
